import debounce from 'lodash/debounce';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';

import { isCountryUkraine } from '@/features/ui/ProfileChapters';

import { SelectOption } from '@/shared/types';

import { IdConstants } from '@/components/Forms/constants';

import { getCitySelect, getDistrictSelect, getStreetSelect } from '@/api/apiService';
import { AddressType, StreetSelectOption } from '@/api/types';

import { getId } from '../helpers';
import { ProfileFormValues } from '../types';

interface Props {
  methods: ReturnType<typeof useForm<ProfileFormValues>>;
  setOptionsDistrictReg: (options: SelectOption[]) => void;
  setOptionsCityReg: (options: SelectOption[]) => void;
  setOptionsStreetReg: (options: StreetSelectOption[]) => void;
  setOptionsDistrictLiving: (options: SelectOption[]) => void;
  setOptionsCityLiving: (options: SelectOption[]) => void;
  setOptionsStreetLiving: (options: StreetSelectOption[]) => void;
  reinitTriggerCount: number;
}

export const useUpdater = ({
  methods,
  setOptionsDistrictReg,
  setOptionsCityReg,
  setOptionsStreetReg,
  setOptionsDistrictLiving,
  setOptionsCityLiving,
  setOptionsStreetLiving,
  reinitTriggerCount,
}: Props): void => {
  const prevValues = useRef<Partial<ProfileFormValues>>({
    countryOfReg: null,
    regionOfReg: null,
    districtOfReg: null,
    countryOfBirth: '',
    cityOfReg: null,
    streetOfReg: null,
    actualCountry: null,
    actualRegion: null,
    actualDistrict: null,
    actualCity: null,
    actualStreet: null,
    REGISTRATION: null,
    LIVING: null,
  });
  const skipWatch = useRef(false);

  const updateFormFields = (fields: Partial<ProfileFormValues>): void => {
    skipWatch.current = true;
    Object.entries(fields).forEach(([field, value]) => {
      methods.setValue(field as keyof ProfileFormValues, value, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    });
  };

  const updateDistrictOptions = async (regionId: string, setter: (options: SelectOption[]) => void): Promise<void> => {
    try {
      const options = await getDistrictSelect(regionId, setter);
      setter(options);
    } catch {
      setter([]);
    }
  };

  const updateCityOptions = async (districtId: string, setter: (options: SelectOption[]) => void): Promise<void> => {
    try {
      const options = await getCitySelect(districtId, setter);
      setter(options);
    } catch {
      setter([]);
    }
  };

  const updateStreetOptions = async (
    cityId: string,
    setter: (options: StreetSelectOption[]) => void,
  ): Promise<void> => {
    try {
      const options = await getStreetSelect(cityId, setter);
      setter(options);
    } catch {
      setter([]);
    }
  };

  useEffect(() => {
    prevValues.current = methods.getValues();
    skipWatch.current = true;
  }, [reinitTriggerCount, methods]);

  const debouncedWatcher = debounce(async (values) => {
    if (skipWatch.current) {
      skipWatch.current = false;
      prevValues.current = { ...values } as Partial<ProfileFormValues>;
      return;
    }

    const {
      countryOfBirth,
      countryOfReg,
      regionOfReg,
      districtOfReg,
      cityOfReg,
      streetOfReg,
      actualCountry,
      actualRegion,
      actualDistrict,
      actualCity,
      actualStreet,
      LIVING,
      REGISTRATION,
      documents,
      actualDocTypeCode,
      registeredDocTypeCode,
    } = values;

    if (REGISTRATION?.code !== prevValues.current.REGISTRATION?.code) {
      updateFormFields({
        registeredDocTypeCode: REGISTRATION?.code,
      });

      if (prevValues.current.REGISTRATION?.code) {
        Object.keys(documents[AddressType.REGISTRATION].info).forEach((key) => {
          methods.setValue(`documents.${AddressType.REGISTRATION}.info.${key}` as keyof ProfileFormValues, '', {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
          });
        });
      }
      methods.setValue(`documents.${AddressType.REGISTRATION}.type` as keyof ProfileFormValues, registeredDocTypeCode, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
    if (LIVING?.code !== prevValues.current.LIVING?.code) {
      updateFormFields({
        actualDocTypeCode: LIVING?.code,
      });

      if (prevValues.current.LIVING?.code) {
        Object.keys(documents[AddressType.LIVING].info).forEach((key) => {
          methods.setValue(`documents.${AddressType.LIVING}.info.${key}` as keyof ProfileFormValues, '', {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
          });
        });
      }
      methods.setValue(`documents.${AddressType.LIVING}.type` as keyof ProfileFormValues, actualDocTypeCode, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }

    if (countryOfBirth !== prevValues.current.countryOfBirth) {
      if (typeof countryOfBirth === 'string' && countryOfBirth.trim() === '') {
        updateFormFields({
          regionOfBirth: '',
          cityOfBirth: '',
          districtOfBirth: '',
        });
      }
    }

    if (countryOfReg?.id !== prevValues.current.countryOfReg?.id) {
      updateFormFields({
        regionOfReg: null,
        districtOfReg: null,
        cityOfReg: null,
        streetOfReg: null,
        houseOfReg: '',
        buildingOfReg: '',
        apartmentOfReg: '',
      });
    }

    if (actualCountry?.id !== prevValues.current.actualCountry?.id) {
      updateFormFields({
        actualRegion: null,
        actualDistrict: null,
        actualCity: null,
        actualStreet: null,
        actualHouse: '',
        actualBuilding: '',
        actualApartment: '',
      });
    }

    const isRegionSpecial = regionOfReg?.id === IdConstants.KYIV || regionOfReg?.id === IdConstants.SEVASTOPOL;
    const isRegionSpecialLiving = actualRegion?.id === IdConstants.KYIV || actualRegion?.id === IdConstants.SEVASTOPOL;

    if (regionOfReg?.id !== prevValues.current.regionOfReg?.id) {
      if (regionOfReg?.id && isRegionSpecial) {
        await updateStreetOptions(regionOfReg?.id, setOptionsStreetReg);
        updateFormFields({
          cityOfReg: regionOfReg as Nullable<SelectOption>,
          districtOfReg: null,
          streetOfReg: null,
          houseOfReg: '',
          buildingOfReg: '',
          apartmentOfReg: '',
        });
      } else {
        if (regionOfReg) {
          await updateDistrictOptions(regionOfReg?.id ?? '', setOptionsDistrictReg);
        } else {
          setOptionsDistrictReg([]);
        }
        updateFormFields({
          districtOfReg: null,
          cityOfReg: null,
          streetOfReg: null,
          houseOfReg: '',
          buildingOfReg: '',
          apartmentOfReg: '',
        });
      }
    }

    if (districtOfReg?.id !== prevValues.current.districtOfReg?.id) {
      if (districtOfReg) {
        await updateCityOptions(districtOfReg?.id ?? '', setOptionsCityReg);
      } else {
        setOptionsCityReg([]);
      }
      updateFormFields({
        cityOfReg: (isRegionSpecial ? regionOfReg : null) as Nullable<SelectOption>,
        streetOfReg: null,
        houseOfReg: '',
        buildingOfReg: '',
        apartmentOfReg: '',
      });
    }

    const currentCityOfRegValue = getId(cityOfReg);
    const prevCityOfRegValue = getId(prevValues.current.cityOfReg);

    if (currentCityOfRegValue !== prevCityOfRegValue) {
      const streetOptionSource = isRegionSpecial && regionOfReg?.id ? regionOfReg.id : currentCityOfRegValue;

      if (streetOptionSource && isCountryUkraine(countryOfReg?.id)) {
        await updateStreetOptions(streetOptionSource, setOptionsStreetReg);
      } else {
        setOptionsStreetReg([]);
      }
      updateFormFields({
        streetOfReg: null,
        houseOfReg: '',
        buildingOfReg: '',
        apartmentOfReg: '',
      });
    }

    if (actualRegion?.id !== prevValues.current.actualRegion?.id) {
      if (actualRegion?.id && isRegionSpecialLiving) {
        await updateStreetOptions(actualRegion?.id, setOptionsStreetLiving);
        updateFormFields({
          actualCity: actualRegion as Nullable<SelectOption>,
          actualDistrict: null,
          actualStreet: null,
          actualHouse: '',
          actualBuilding: '',
          actualApartment: '',
        });
      } else {
        if (actualRegion?.id) {
          await updateDistrictOptions(actualRegion.id ?? '', setOptionsDistrictLiving);
        } else {
          setOptionsDistrictLiving([]);
        }
        updateFormFields({
          actualDistrict: null,
          actualCity: null,
          actualStreet: null,
          actualHouse: '',
          actualBuilding: '',
          actualApartment: '',
        });
      }
    }

    if (actualDistrict?.id !== prevValues.current.actualDistrict?.id) {
      if (actualDistrict?.id) {
        await updateCityOptions(actualDistrict.id ?? '', setOptionsCityLiving);
      } else {
        setOptionsCityLiving([]);
      }
      updateFormFields({
        actualCity: (isRegionSpecialLiving ? actualRegion : null) as Nullable<SelectOption>,
        actualStreet: null,
        actualHouse: '',
        actualBuilding: '',
        actualApartment: '',
      });
    }

    const currentActualCityValue = getId(actualCity);
    const prevActualCityValue = getId(prevValues.current.actualCity);

    if (currentActualCityValue !== prevActualCityValue) {
      const streetOptionSource = isRegionSpecialLiving && actualRegion?.id ? actualRegion.id : currentActualCityValue;

      if (streetOptionSource && isCountryUkraine(actualCountry?.id)) {
        await updateStreetOptions(
          isRegionSpecialLiving ? (actualRegion?.id ?? '') : (typeof actualCity !== 'string' && actualCity?.id) || '',
          setOptionsStreetLiving,
        );
      } else {
        setOptionsStreetLiving([]);
      }
      updateFormFields({
        actualStreet: null,
        actualHouse: '',
        actualBuilding: '',
        actualApartment: '',
      });
    }

    prevValues.current = {
      countryOfReg,
      regionOfReg,
      districtOfReg: isRegionSpecial ? null : districtOfReg,
      cityOfReg,
      streetOfReg,
      actualCountry,
      actualRegion,
      actualDistrict: isRegionSpecialLiving ? null : actualDistrict,
      actualCity,
      actualStreet,
      LIVING,
      REGISTRATION,
      documents,
      actualDocTypeCode,
      registeredDocTypeCode,
    } as Partial<ProfileFormValues>;
  }, 300);

  useEffect(() => {
    const subscription = methods.watch((values) => debouncedWatcher(values));

    return () => subscription.unsubscribe();
  }, [
    methods,
    setOptionsDistrictReg,
    setOptionsCityReg,
    setOptionsStreetReg,
    setOptionsDistrictLiving,
    setOptionsCityLiving,
    setOptionsStreetLiving,
  ]);
};
